import { supabase } from '@/integrations/supabase/client';
import { UserIdentity } from '@/types';
import { handleDatabaseError, handleAuthError } from '@/lib/errorHandler';

export const identityService = {
  /**
   * Criar nova identidade aspiracional
   */
  async createIdentity(identityStatement: string, pillar?: string): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        handleAuthError(new Error('Usuário não autenticado'));
        throw new Error('Usuário não autenticado');
      }

      const { data, error } = await supabase
        .from('user_identity')
        .insert({
          user_id: user.id,
          identity_statement: identityStatement,
          pillar,
          votes_cast: 0
        })
        .select()
        .single();

      if (error) {
        handleDatabaseError(error);
        throw error;
      }

      return data.id;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Buscar identidades do usuário
   */
  async getUserIdentities(): Promise<UserIdentity[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_identity')
        .select('*')
        .eq('user_id', user.id)
        .order('votes_cast', { ascending: false });

      if (error) {
        handleDatabaseError(error);
        throw error;
      }

      return (data || []).map(d => ({
        id: d.id,
        userId: d.user_id,
        identityStatement: d.identity_statement,
        pillar: (d.pillar as any) || undefined,
        votesCast: d.votes_cast,
        createdAt: d.created_at,
        updatedAt: d.updated_at
      }));
    } catch (error) {
      handleDatabaseError(error);
      return [];
    }
  },

  /**
   * Atualizar declaração de identidade
   */
  async updateIdentity(identityId: string, updates: { identityStatement?: string; pillar?: string }): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const updateData: any = {};
      if (updates.identityStatement) updateData.identity_statement = updates.identityStatement;
      if (updates.pillar) updateData.pillar = updates.pillar;

      const { error } = await supabase
        .from('user_identity')
        .update(updateData)
        .eq('id', identityId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Deletar identidade
   */
  async deleteIdentity(identityId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('user_identity')
        .delete()
        .eq('id', identityId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Incrementar votos manualmente
   */
  async castVote(identityId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Buscar votes_cast atual e incrementar
      const { data: current } = await supabase
        .from('user_identity')
        .select('votes_cast')
        .eq('id', identityId)
        .eq('user_id', user.id)
        .single();

      const { error } = await supabase
        .from('user_identity')
        .update({ 
          votes_cast: (current?.votes_cast || 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', identityId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao incrementar voto de identidade:', error);
    }
  }
};
